import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppHeader } from '@/components/layout/AppHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { usePendingAcknowledgements, type PendingAcknowledgement } from '@/hooks/usePendingAcknowledgements';
import { useAcknowledgePolicy } from '@/hooks/useAcknowledgePolicy';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileText, ExternalLink, Shield, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { PdfViewerDialog } from '@/components/policies/PdfViewerDialog';
import { useUserRole } from '@/hooks/useUserRole';
import { toast } from 'sonner';

export default function PendingAcknowledgements() {
  const navigate = useNavigate();
  const { data: pending = [], isLoading } = usePendingAcknowledgements();
  const queryClient = useQueryClient();
  const { data: userRole } = useUserRole();
  const [acknowledgingId, setAcknowledgingId] = useState<string | null>(null);
  const [viewingDocument, setViewingDocument] = useState<{ url: string; name: string } | null>(null);
  const hasRedirected = useRef(false);

  const handleAcknowledge = async (item: PendingAcknowledgement) => {
    setAcknowledgingId(item.version_id);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!profile) throw new Error('Profile not found');

      const { error } = await supabase
        .from('policy_acknowledgements')
        .insert({
          policy_version_id: item.version_id,
          acknowledged_by: profile.id,
        });

      if (error) throw error;

      // Invalidate queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['pending-acknowledgements'] });
      queryClient.invalidateQueries({ queryKey: ['policy-acknowledgements'] });
    } catch (error) {
      // 23505 = unique_violation: this version is already acknowledged
      // (stale list, double-submit, or a second tab). The record exists,
      // so clear it from the list instead of trapping the user on this gate.
      if ((error as { code?: string } | null)?.code === '23505') {
        queryClient.invalidateQueries({ queryKey: ['pending-acknowledgements'] });
        queryClient.invalidateQueries({ queryKey: ['policy-acknowledgements'] });
        toast.info('You have already acknowledged this policy.');
      } else {
        console.error('Failed to acknowledge:', error);
        toast.error('Could not record your acknowledgement. Please try again.');
      }
    } finally {
      setAcknowledgingId(null);
    }
  };

  const handleViewDocument = (fileUrl: string, policyName: string) => {
    setViewingDocument({ url: fileUrl, name: policyName });
  };

  // Auto-redirect to dashboard after all acknowledgements are complete
  useEffect(() => {
    if (!isLoading && pending.length === 0 && !hasRedirected.current) {
      hasRedirected.current = true;
      const timer = setTimeout(() => {
        navigate(userRole === 'staff' ? '/staff-dashboard' : '/dashboard', { replace: true });
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isLoading, pending.length, navigate, userRole]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <main className="container px-4 py-8">
          <div className="space-y-4">
            <Skeleton className="h-8 w-[300px]" />
            <Skeleton className="h-[200px]" />
            <Skeleton className="h-[200px]" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      
      <main className="container px-4 py-8 max-w-3xl">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-[28px] font-extrabold tracking-tight">Policy Acknowledgements Required</h1>
          <p className="mt-2 text-muted-foreground">
            Please review and acknowledge the following policies before continuing.
          </p>
        </div>

        {pending.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center py-12">
              <CheckCircle2 className="h-12 w-12 text-primary mb-4" />
              <p className="text-lg font-medium">All policies acknowledged!</p>
              <p className="text-muted-foreground">Redirecting to dashboard...</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              {pending.length} {pending.length === 1 ? 'policy' : 'policies'} pending acknowledgement
            </p>

            {pending.map((item) => (
              <Card key={item.version_id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <CardTitle className="text-base">{item.policy_name}</CardTitle>
                        <CardDescription className="mt-1">
                          {item.policy_category}
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="secondary">v{item.version_number}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => handleViewDocument(item.file_url, item.policy_name)}
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      View Document
                    </Button>
                    <Button
                      className="flex-1"
                      onClick={() => handleAcknowledge(item)}
                      disabled={acknowledgingId === item.version_id}
                    >
                      {acknowledgingId === item.version_id ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Acknowledging...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                          Acknowledge
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <PdfViewerDialog
          open={!!viewingDocument}
          onOpenChange={(open) => !open && setViewingDocument(null)}
          fileUrl={viewingDocument?.url || ''}
          title={viewingDocument?.name}
        />
      </main>
    </div>
  );
}
